import { cn } from "@/lib/utils";
import { CaretRight, CircleNotch } from "@phosphor-icons/react";
import { formatDateShort } from "./payout-helpers";
import { formatMoney } from "@/lib/admin-payment-breakdown";
import { PayoutAvatar } from "./primitives";

import type { Payout, PayoutDetail } from "@/types";

type PayoutAmounts = Pick<Payout, "amount" | "grossAmount" | "totalWithheld">;

/** Bruto, obustave i isplaćeno u tooltipu iznosa, da tabela ostane pregledna. */
export function payoutAmountTooltip(payout: PayoutAmounts): string {
  return payout.totalWithheld > 0
    ? `Bruto ${formatMoney(payout.grossAmount)} KM, obustave ${formatMoney(payout.totalWithheld)} KM, isplaćeno ${formatMoney(payout.amount)} KM`
    : `Isplaćeno ${formatMoney(payout.amount)} KM`;
}

/**
 * Zbir ispod stavki isplate. Zarada po stavkama je bruto, pa se zbir poklapa
 * sa bruto iznosom; obustave i isplaćeno na račun idu odvojeno. Bez obustava
 * (faktura) bruto je isto što i isplaćeno, pa ostaje jedna vrijednost.
 */
export function PayoutTotals({ payout }: { payout: PayoutAmounts }) {
  return (
    <dl className="flex flex-wrap justify-end gap-x-5 gap-y-1 px-1 text-[12px] tabular-nums">
      {payout.totalWithheld > 0 && (
        <>
          <div className="flex gap-1.5">
            <dt className="text-muted-foreground">Bruto</dt>
            <dd className="font-medium text-foreground">{formatMoney(payout.grossAmount)} KM</dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-muted-foreground">Obustave</dt>
            <dd className="font-medium text-foreground">{formatMoney(payout.totalWithheld)} KM</dd>
          </div>
        </>
      )}
      <div className="flex gap-1.5">
        <dt className="text-muted-foreground">Isplaćeno</dt>
        <dd className="font-semibold text-foreground">{formatMoney(payout.amount)} KM</dd>
      </div>
    </dl>
  );
}

interface PayoutDetailRowProps {
  payout: Payout;
  isLast: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  detail?: PayoutDetail;
  detailLoading: boolean;
}

export function PayoutDetailRow({
  payout,
  isLast,
  isExpanded,
  onToggle,
  detail,
  detailLoading,
}: PayoutDetailRowProps) {
  return (
    <>
      <tr
        onClick={onToggle}
        className={cn(
          "group cursor-pointer transition-colors",
          isExpanded ? "bg-primary/[0.04]" : "hover:bg-primary/[0.03]",
          !isLast && !isExpanded && "border-b border-border/40",
        )}
      >
        <td className="px-3 py-3">
          <CaretRight
            className={cn(
              "size-3.5 text-muted-foreground transition-transform duration-200",
              isExpanded && "rotate-90 text-primary",
            )}
          />
        </td>
        <td className="whitespace-nowrap px-3 py-3 font-medium text-foreground">
          {formatDateShort(payout.createdAt)}
        </td>
        <td className="px-3 py-3">
          <div className="flex items-center gap-2">
            <PayoutAvatar
              name={payout.therapistName}
              tone="primary"
              className="size-6 text-[9px]"
            />
            <span className="truncate font-medium text-foreground">
              {payout.therapistName}
            </span>
          </div>
        </td>
        <td
          className="whitespace-nowrap px-3 py-3 text-right tabular-nums font-semibold text-foreground"
          title={payoutAmountTooltip(payout)}
        >
          {formatMoney(payout.amount)}
          {payout.amount != null && (
            <span className="ml-0.5 text-[11px] font-normal text-muted-foreground">KM</span>
          )}
          {payout.totalWithheld > 0 && (
            <span className="block text-[11px] font-normal text-muted-foreground">
              bruto {formatMoney(payout.grossAmount)}
            </span>
          )}
        </td>
        <td className="px-3 py-3 text-right tabular-nums text-muted-foreground">
          {payout.sessionCount}
        </td>
        <td className="truncate px-3 py-3 text-muted-foreground">
          {payout.adminName}
        </td>
        <td className="px-3 py-3 text-muted-foreground">
          {payout.note ? (
            <span className="line-clamp-1">{payout.note}</span>
          ) : (
            <span className="text-muted-foreground/40">—</span>
          )}
        </td>
      </tr>

      {isExpanded && (
        <tr>
          <td
            colSpan={7}
            className={cn(
              "bg-muted/20 px-4 py-3.5",
              !isLast && "border-b border-border/40",
            )}
          >
            {detailLoading ? (
              <div className="flex items-center justify-center py-4">
                <CircleNotch className="size-5 animate-spin text-primary" />
              </div>
            ) : detail?.sessions ? (
              <div className="space-y-2">
                <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
                  Stavke u isplati ({detail.sessions.length})
                </p>
                <div className="overflow-hidden rounded-xl border border-border/40 bg-card">
                  <table className="w-full text-[12px]">
                    <thead>
                      <tr className="bg-muted/30">
                        <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                          Datum
                        </th>
                        <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                          Klijent
                        </th>
                        <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                          Usluga
                        </th>
                        <th className="px-3 py-1.5 text-right text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                          Cijena
                        </th>
                        <th className="px-3 py-1.5 text-right text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                          Zarada
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.sessions.map((s, i) => (
                        <tr
                          key={s.id}
                          className={cn(
                            i !== detail.sessions.length - 1 &&
                              "border-b border-border/30",
                          )}
                        >
                          <td className="whitespace-nowrap px-3 py-1.5 text-foreground">
                            {formatDateShort(s.scheduledAt)}
                          </td>
                          <td className="px-3 py-1.5 text-foreground">
                            {s.clientName}
                          </td>
                          <td className="px-3 py-1.5 text-muted-foreground">
                            {s.serviceName}
                          </td>
                          <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-foreground">
                            {formatMoney(s.price)}
                            {s.price != null && (
                              <span className="ml-0.5 text-[11px] text-muted-foreground">KM</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums font-medium text-foreground">
                            {formatMoney(s.therapistEarnings)}
                            {s.therapistEarnings != null && (
                              <span className="ml-0.5 text-[11px] font-normal text-muted-foreground">KM</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <PayoutTotals payout={detail} />
              </div>
            ) : null}
          </td>
        </tr>
      )}
    </>
  );
}
